"use client";

import React, { FC, useState, useEffect } from "react";
import ButtonCircle from "@/shared/ButtonCircle";
import rightImg from "@/images/SVG-subcribe2.png";
import Badge from "@/shared/Badge";
import Input from "@/shared/Input";
import Image from "next/image";
import { useAppDispatch, useAppSelector } from '../app/lib/hooks';
import { addNewsletter } from '../app/lib/reducers/newsletterReducer';
import { useTranslation } from "react-i18next";

export interface SectionSubscribe2Props {
  className?: string;
}

const SectionSubscribe2: FC<SectionSubscribe2Props> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const { loading, error, success } = useAppSelector(state => state.newsletter);
  const [email, setEmail] = useState<string>('');
  const language = useAppSelector(state => state.language.selectedLanguage ||'en_US');
  const [origin] = useState<string>('website'); // Assuming default origin as 'website'
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const { t } = useTranslation("guydeez");

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const origin = window.location.href; // Get the current URL
    dispatch(addNewsletter(email, language, origin));
  };

  useEffect(() => {
    if (success) {
      setShowSuccessMessage(true);
      const successTimer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000); // Hide the message after 3 seconds
      return () => clearTimeout(successTimer); // Cleanup the timer on unmount
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      setShowErrorMessage(true);
      const errorTimer = setTimeout(() => {
        setShowErrorMessage(false);
      }, 3000); // Hide the message after 3 seconds
      return () => clearTimeout(errorTimer); // Cleanup the timer on unmount
    }
  }, [error]);

  return (
    <div
      className={`nc-SectionSubscribe2 relative flex flex-col lg:flex-row lg:items-center ${className}`}
      data-nc-id="SectionSubscribe2"
    >
      <div className="flex-shrink-0 mb-10 lg:mb-0 lg:mr-10 lg:w-2/5">
        <h2 className="font-semibold text-4xl">{t('components_newsletter_join_our_newsletter_title')}</h2>
        <span className="block mt-5 text-neutral-500 dark:text-neutral-400">
        {t('components_newsletter_join_our_newsletter_subtitle')}
        </span>
        <ul className="space-y-4 mt-10">
          <li className="flex items-center space-x-4">
            <Badge name="01" />
            <span className="font-medium text-neutral-700 dark:text-neutral-300">
              {t('components_newsletter_be_noticed_newest_destinations')}
            </span>
          </li>
          <li className="flex items-center space-x-4">
            <Badge color="red" name="02" />
            <span className="font-medium text-neutral-700 dark:text-neutral-300">
              {t('components_newsletter_be_aware_latest_post')}
            </span>
          </li>
        </ul>
        <form className="mt-10 relative max-w-sm" onSubmit={handleSubmit}>
          <Input
            required
            aria-required
            placeholder={t('components_newsletter_enter_your_email')}
            type="email"
            rounded="rounded-full"
            sizeClass="h-12 px-5 py-3"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <ButtonCircle
            type="submit"
            aria-label={t('components_newsletter_subscribe', { defaultValue: 'Subscribe' }) as string}
            className="absolute transform top-1/2 -translate-y-1/2 right-1.5"
            size="w-10 h-10"
            disabled={loading}
          >
            <i className="las la-arrow-right text-xl" aria-hidden="true"></i>
          </ButtonCircle>
        </form>
        {showSuccessMessage && <p className="text-green-500 mt-2">{t('components_newsletter_successfully_sub')}</p>}
        {showErrorMessage && <p className="text-red-500 mt-2">{error}</p>}
      </div>
      <div className="flex-grow">
        <Image alt="" src={rightImg} />
      </div>
    </div>
  );
};

export default SectionSubscribe2;
